<template>
  <div class="app-container work-log-page">
    <el-form ref="queryForm" :model="queryParams" v-show="showSearch" label-width="86px">
      <el-row :gutter="10" class="mb8">
        <el-col :xs="24" :md="12" :lg="6">
          <el-form-item label="日志模板" prop="templateId">
            <el-select v-model="queryParams.templateId" placeholder="请选择模板" clearable filterable style="width:100%">
              <el-option v-for="item in templateFilterOptions" :key="item.id" :label="filterTemplateName(item)" :value="item.id" />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :xs="24" :md="12" :lg="6">
          <el-form-item label="日志状态" prop="status">
            <el-select v-model="queryParams.status" placeholder="请选择状态" clearable style="width:100%">
              <el-option v-for="item in statusOptions" :key="item.value" :label="item.label" :value="item.value" />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :xs="24" :md="12" :lg="8">
          <el-form-item label="日志日期" prop="dateRange">
            <el-date-picker
              v-model="queryParams.dateRange"
              value-format="yyyy-MM-dd"
              type="daterange"
              range-separator="-"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              style="width:100%"
            />
          </el-form-item>
        </el-col>
        <el-col :xs="24" :md="12" :lg="6">
          <el-form-item label="关键词" prop="keyword">
            <el-input v-model="queryParams.keyword" placeholder="标题/内容关键词" clearable @keyup.enter.native="handleQuery" />
          </el-form-item>
        </el-col>
        <el-col :xs="24" :md="24" :lg="24" align="right">
          <el-form-item>
            <el-button type="primary" icon="el-icon-search" @click="handleQuery">搜索</el-button>
            <el-button icon="el-icon-refresh" @click="resetQuery">清空</el-button>
          </el-form-item>
        </el-col>
      </el-row>
    </el-form>

    <el-row :gutter="10" class="mb8">
      <el-col :span="1.5">
        <el-button v-hasPermi="['crm:workLog:add']" type="primary" icon="el-icon-plus" @click="handleAdd">写日志</el-button>
      </el-col>
      <right-toolbar :showSearch.sync="showSearch" @queryTable="getList"></right-toolbar>
    </el-row>

    <el-table v-loading="loading" :data="dataList" height="calc(100vh - 300px)" class="work-log-table">
      <el-table-column label="日志" min-width="360" fixed="left">
        <template slot-scope="scope">
          <div class="log-title-cell">
            <div class="log-row-title">{{ displayLogTitle(scope.row) }}</div>
            <div class="log-row-meta">
              <el-tag size="mini" effect="plain">{{ displayLogTemplateName(scope.row) }}</el-tag>
              <span>{{ periodText(scope.row) }}</span>
            </div>
            <div class="log-row-preview">{{ contentPreview(scope.row) }}</div>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="状态" width="90" align="center">
        <template slot-scope="scope">
          <el-tag :type="statusTag(scope.row.status)" size="mini">{{ scope.row.statusDesc || statusText(scope.row.status) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="发送给" prop="receiverSummary" min-width="160" show-overflow-tooltip />
      <el-table-column label="提交时间" width="170" align="center">
        <template slot-scope="scope">{{ displayMinuteTime(scope.row.submitTime) }}</template>
      </el-table-column>
      <el-table-column label="创建时间" prop="createTime" width="170" align="center" />
      <el-table-column label="操作" width="230" fixed="right" align="center" class-name="small-padding fixed-width">
        <template slot-scope="scope">
          <el-button type="text" @click="handleDetail(scope.row)">详情</el-button>
          <el-button v-if="scope.row.canEdit" type="text" @click="handleEdit(scope.row)">修改日志</el-button>
          <el-button v-if="scope.row.canEdit && isDraftLog(scope.row)" v-hasPermi="['crm:workLog:submit']" type="text" @click="submitExisting(scope.row)">提交</el-button>
          <el-button v-if="scope.row.canEdit && isDraftLog(scope.row)" v-hasPermi="['crm:workLog:remove']" type="text" class="danger" @click="handleRemove(scope.row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <pagination v-show="total>0" :total="total" :page.sync="queryParams.pageNum" :limit.sync="queryParams.pageSize" @pagination="getList" />

    <el-dialog :title="formTitle" :visible.sync="formOpen" width="940px" append-to-body :close-on-click-modal="false">
      <el-form ref="logForm" :model="form" label-width="102px" class="work-log-form">
        <el-row :gutter="12">
          <el-col :xs="24" :md="12">
            <el-form-item label="日志模板" required>
              <el-select v-model="form.templateId" placeholder="请选择模板" filterable style="width:100%" @change="onTemplateChange">
                <el-option v-for="item in templateOptions" :key="item.id" :label="item.templateName" :value="item.id" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :xs="24" :md="12">
            <el-form-item label="日志标题">
              <el-input v-model="form.title" maxlength="120" placeholder="不填时系统按日期和模板生成" />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :md="12">
            <el-form-item label="发送给" :required="viewerConfig.receiverRequiredFlag === 1">
              <div class="viewer-strip">
                <div v-for="user in receiverDisplayUsers" :key="`receiver_${user.id}`" class="viewer-card">
                  <div class="viewer-avatar-wrap">
                    <img v-if="user.avatar" class="viewer-avatar-img" :src="user.avatar" />
                    <div v-else class="viewer-avatar-fallback">{{ user.shortName }}</div>
                    <span v-if="user.fixed" class="viewer-lock"><i class="el-icon-lock"></i></span>
                    <button v-else class="viewer-remove" type="button" @click.prevent="removeReceiverUser(user.id)">
                      <i class="el-icon-close"></i>
                    </button>
                  </div>
                  <div class="viewer-name">{{ user.name }}</div>
                </div>
                <el-popover
                  v-if="viewerConfig.allowCustomViewer !== 0"
                  v-model="receiverPickerOpen"
                  placement="bottom-start"
                  width="360"
                  trigger="manual"
                  popper-class="work-log-viewer-popover"
                >
                  <el-select
                    v-model="form.receiverUserIds"
                    multiple
                    filterable
                    clearable
                    placeholder="请选择额外发送给"
                    style="width:100%"
                    @change="handleReceiverChange"
                  >
                    <el-option v-for="user in selectableReceiverUsers" :key="user.userId" :label="user.nickName" :value="user.userId" />
                  </el-select>
                  <div class="viewer-picker-actions">
                    <el-button size="mini" type="primary" @click="receiverPickerOpen = false">完成</el-button>
                  </div>
                  <div slot="reference" class="viewer-add-card" @click.stop="toggleReceiverPicker">
                    <i class="el-icon-plus"></i>
                  </div>
                </el-popover>
              </div>
            </el-form-item>
          </el-col>
          <el-col v-if="!isSubmittedEdit" :xs="24" :md="12">
            <el-form-item label="定时发送">
              <el-switch
                v-model="scheduleEnabled"
                active-text="开启"
                inactive-text="关闭"
                @change="handleScheduleSwitch"
              />
            </el-form-item>
          </el-col>
          <el-col v-if="scheduleEnabled && !isSubmittedEdit" :xs="24" :md="12">
            <el-form-item label="发送时间" required>
              <el-date-picker
                v-model="scheduleSubmitTime"
                type="datetime"
                value-format="yyyy-MM-dd HH:mm:ss"
                format="yyyy-MM-dd HH:mm"
                placeholder="请选择5分钟后的时间"
                :picker-options="schedulePickerOptions"
                @change="handleScheduleTimeChange"
                style="width:100%"
              />
            </el-form-item>
          </el-col>
          <el-col v-for="field in formFields" :key="field.fieldKey" :span="24">
            <el-form-item :label="field.fieldName" :required="field.requiredFlag === 1">
              <el-input
                v-if="field.fieldType === 'textarea'"
                v-model="form.contentMap[field.fieldKey]"
                type="textarea"
                :rows="4"
                :maxlength="field.maxLength || 3000"
                show-word-limit
                :placeholder="field.placeholder || '请输入'"
              />
              <el-input
                v-else-if="field.fieldType === 'text'"
                v-model="form.contentMap[field.fieldKey]"
                :maxlength="field.maxLength || 500"
                :placeholder="field.placeholder || '请输入'"
              />
              <el-input-number
                v-else-if="field.fieldType === 'number'"
                v-model="form.contentMap[field.fieldKey]"
                controls-position="right"
                style="width:100%"
              />
              <el-date-picker
                v-else-if="field.fieldType === 'date'"
                v-model="form.contentMap[field.fieldKey]"
                value-format="yyyy-MM-dd"
                type="date"
                placeholder="请选择日期"
                style="width:100%"
              />
              <el-select
                v-else-if="field.fieldType === 'select'"
                v-model="form.contentMap[field.fieldKey]"
                clearable
                filterable
                :placeholder="field.placeholder || '请选择'"
                style="width:100%"
              >
                <el-option v-for="option in fieldOptions(field)" :key="option.value" :label="option.label" :value="option.value" />
              </el-select>
              <el-select
                v-else-if="field.fieldType === 'multiselect'"
                v-model="form.contentMap[field.fieldKey]"
                multiple
                clearable
                filterable
                :placeholder="field.placeholder || '请选择'"
                style="width:100%"
              >
                <el-option v-for="option in fieldOptions(field)" :key="option.value" :label="option.label" :value="option.value" />
              </el-select>
              <file-upload
                v-else-if="isUploadField(field)"
                v-model="form.contentMap[field.fieldKey]"
                :limit="5"
                :accept="uploadAccept(field)"
                :readOnly="false"
                @on-change="handleFieldFileChange(field.fieldKey, $event)"
              />
              <el-input
                v-else
                v-model="form.contentMap[field.fieldKey]"
                type="textarea"
                :rows="3"
                :maxlength="field.maxLength || 1000"
                show-word-limit
                :placeholder="field.placeholder || '请输入'"
              />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="formOpen = false">取消</el-button>
        <el-button type="primary" :loading="submitLoading" @click="submitForm(true)">{{ isSubmittedEdit ? '保存修改' : (scheduleEnabled ? '定时发送' : '提交日志') }}</el-button>
      </span>
    </el-dialog>

    <work-log-detail-dialog :visible.sync="detailOpen" :log-id="detailId" @refresh="getList" @edit="handleDetailEdit" />
  </div>
</template>

<script>
import {
  getWorkLogDetail,
  listMyWorkLogs,
  removeWorkLog,
  saveWorkLogDraft,
  scheduleSubmitWorkLog,
  submitWorkLog
} from '@/api/crm/workLog'
import {
  getWorkLogTemplateDetail,
  listWorkLogTemplateOptions,
  previewWorkLogTemplateViewers
} from '@/api/crm/workLogTemplate'
import { listUserWithOutDataScope } from '@/api/system/user'
import WorkLogDetailDialog from './components/detailDialog'
import FileUpload from '@/views/crm/components/fileUpload'

const ACTIVE_TENANT_USER_QUERY = { pageNum: 1, pageSize: 999, tenantUserStatus: 1 }
const MIN_SCHEDULE_DELAY_MS = 5 * 60 * 1000
const DEFAULT_SCHEDULE_DELAY_MS = 10 * 60 * 1000
const BLANK_TEMPLATE_CODE = 'BLANK_LOG'
const BLANK_FILTER_NAME = '其他'
const BLANK_LOG_DISPLAY_NAME = '其他日志'

const emptyForm = () => ({
  id: null,
  status: null,
  templateId: null,
  logType: 'DAILY',
  periodDate: '',
  periodStartDate: '',
  periodEndDate: '',
  periodRange: [],
  title: '',
  contentMap: {},
  receiverUserIds: [],
  ccUserIds: [],
  attachmentFileIds: ''
})

export default {
  name: 'WorkLogMy',
  components: {
    WorkLogDetailDialog,
    FileUpload
  },
  data() {
    return {
      loading: false,
      submitLoading: false,
      showSearch: true,
      formOpen: false,
      receiverPickerOpen: false,
      detailOpen: false,
      detailId: null,
      total: 0,
      dataList: [],
      templateOptions: [],
      staffOptions: [],
      form: emptyForm(),
      scheduleEnabled: false,
      scheduleSubmitTime: this.defaultScheduleSubmitTime(),
      currentTemplate: {},
      formFields: [],
      viewerConfig: {
        allowCustomViewer: 1,
        receiverRequiredFlag: 1,
        leaderVisibleFlag: 0,
        currentUserId: null,
        fixedReceiverUserIds: [],
        fixedCcUserIds: [],
        leaderUserIds: [],
        receiverUserIds: [],
        ccUserIds: []
      },
      queryParams: {
        pageNum: 1,
        pageSize: 10,
        templateId: null,
        status: null,
        keyword: '',
        dateRange: []
      },
      autoSaveTimer: null,
      autoSavePaused: false,
      autoSaveSaving: false,
      autoSaveLastSnapshot: '',
      statusOptions: [
        { value: 1, label: '未提交/待定时' },
        { value: 2, label: '已提交' }
      ]
    }
  },
  computed: {
    templateFilterOptions() {
      return (this.templateOptions || []).slice().sort((a, b) => {
        if (this.isBlankTemplate(a) === this.isBlankTemplate(b)) {
          return 0
        }
        return this.isBlankTemplate(a) ? 1 : -1
      })
    },
    formTitle() {
      return this.form.id ? '编辑日志' : '写日志'
    },
    isSubmittedEdit() {
      return Number(this.form.status) === 2
    },
    fixedViewerIds() {
      return this.mergeIds(this.viewerConfig.fixedReceiverUserIds)
    },
    receiverDisplayUsers() {
      return [
        ...this.viewerCards(this.viewerConfig.fixedReceiverUserIds, true),
        ...this.viewerCards(this.form.receiverUserIds, false)
      ]
    },
    selectableReceiverUsers() {
      return this.selectableUsers()
    },
    schedulePickerOptions() {
      const min = this.futureScheduleDate(MIN_SCHEDULE_DELAY_MS)
      const startOfMinDate = new Date(min.getFullYear(), min.getMonth(), min.getDate()).getTime()
      const selected = this.parseDateTime(this.scheduleSubmitTime) || min
      const sameMinDate = selected.getFullYear() === min.getFullYear() &&
        selected.getMonth() === min.getMonth() &&
        selected.getDate() === min.getDate()
      const rangeStart = sameMinDate ? this.formatClock(min) : '00:00:00'
      return {
        disabledDate(time) {
          return time.getTime() < startOfMinDate
        },
        selectableRange: `${rangeStart} - 23:59:59`
      }
    }
  },
  watch: {
    form: {
      deep: true,
      handler() {
        this.scheduleAutoSave()
      }
    },
    formOpen(value, oldValue) {
      if (!value && oldValue) {
        this.flushAutoSave(true)
      }
    }
  },
  created() {
    this.initPage()
  },
  beforeDestroy() {
    this.flushAutoSave(true)
  },
  methods: {
    isBlankTemplate(item) {
      if (!item) {
        return false
      }
      return item.templateCode === BLANK_TEMPLATE_CODE || item.templateName === '空白日志'
    },
    filterTemplateName(item) {
      return this.isBlankTemplate(item) ? BLANK_FILTER_NAME : (item.templateName || '未命名模板')
    },
    isBlankLog(row) {
      return row && (row.templateCode === BLANK_TEMPLATE_CODE || row.templateNameSnapshot === '空白日志' || row.templateName === '空白日志')
    },
    displayLogTitle(row) {
      const title = (row && row.title) || '未命名日志'
      return this.isBlankLog(row) ? title.replace(/空白日志/g, BLANK_LOG_DISPLAY_NAME) : title
    },
    displayLogTemplateName(row) {
      return this.isBlankLog(row) ? BLANK_FILTER_NAME : ((row && (row.templateNameSnapshot || row.templateName)) || '-')
    },
    initPage() {
      this.loadTemplateOptions()
      this.loadUsers()
      this.getList()
    },
    loadTemplateOptions() {
      listWorkLogTemplateOptions().then(response => {
        this.templateOptions = (response && response.data) || []
      })
    },
    loadUsers() {
      listUserWithOutDataScope(ACTIVE_TENANT_USER_QUERY).then(response => {
        this.staffOptions = (response && response.rows) || []
      })
    },
    getList() {
      this.loading = true
      listMyWorkLogs(this.buildQueryParams())
        .then(response => {
          this.dataList = (response && response.rows) || []
          this.total = (response && response.total) || 0
        })
        .finally(() => {
          this.loading = false
        })
    },
    buildQueryParams() {
      const params = { ...this.queryParams }
      if (params.dateRange && params.dateRange.length > 1) {
        params.beginDate = params.dateRange[0]
        params.endDate = params.dateRange[1]
      }
      delete params.dateRange
      return this.cleanParams(params)
    },
    handleQuery() {
      this.queryParams.pageNum = 1
      this.getList()
    },
    resetQuery() {
      this.queryParams = {
        pageNum: 1,
        pageSize: 10,
        templateId: null,
        status: 2,
        keyword: '',
        dateRange: []
      }
      this.getList()
    },
    handleAdd() {
      this.resetAutoSaveState(true)
      this.form = emptyForm()
      this.form.periodDate = this.today()
      this.formFields = []
      this.resetSchedule()
      this.currentTemplate = {}
      this.receiverPickerOpen = false
      this.viewerConfig = {
        allowCustomViewer: 1,
        receiverRequiredFlag: 1,
        leaderVisibleFlag: 0,
        currentUserId: null,
        fixedReceiverUserIds: [],
        fixedCcUserIds: [],
        leaderUserIds: [],
        receiverUserIds: [],
        ccUserIds: []
      }
      this.formOpen = true
      if (this.templateOptions.length === 1) {
        this.onTemplateChange(this.templateOptions[0].id)
      }
      this.$nextTick(() => {
        this.autoSavePaused = false
        this.autoSaveLastSnapshot = this.autoSavePayloadSnapshot()
      })
    },
    handleEdit(row) {
      if (!row || !row.id) {
        return
      }
      this.submitLoading = true
      getWorkLogDetail(row.id)
        .then(response => {
          const detail = (response && response.data) || {}
          this.form = {
            ...emptyForm(),
            id: detail.id,
            status: detail.status,
            templateId: detail.templateId,
            logType: detail.logType || 'DAILY',
            periodDate: detail.periodDate || '',
            periodStartDate: detail.periodStartDate || '',
            periodEndDate: detail.periodEndDate || '',
            periodRange: detail.periodStartDate && detail.periodEndDate ? [detail.periodStartDate, detail.periodEndDate] : [],
            title: detail.title || '',
            receiverUserIds: this.normalizeIds(detail.receiverUserIds),
            ccUserIds: [],
            attachmentFileIds: detail.attachmentFileIds || '',
            contentMap: {}
          }
          this.resetSchedule(detail)
          return this.loadTemplate(detail.templateId).then(() => {
            const content = {}
            const valueMap = {}
            ;(detail.fieldValues || []).forEach(item => {
              valueMap[item.fieldKey] = item.value
            })
            this.formFields.forEach(field => {
              this.$set(content, field.fieldKey, valueMap[field.fieldKey] !== undefined ? valueMap[field.fieldKey] : this.defaultValue(field))
            })
            this.form.contentMap = content
            this.formOpen = true
          })
        })
        .finally(() => {
          this.submitLoading = false
        })
    },
    handleDetail(row) {
      this.detailId = row.id
      this.detailOpen = true
    },
    handleDetailEdit(detail) {
      this.detailOpen = false
      this.handleEdit(detail)
    },
    onTemplateChange(templateId) {
      this.form.templateId = templateId
      this.loadTemplate(templateId).then(() => this.restoreLatestAutoSave(templateId))
    },
    loadTemplate(templateId) {
      if (!templateId) {
        this.currentTemplate = {}
        this.formFields = []
        return Promise.resolve()
      }
      return Promise.all([
        getWorkLogTemplateDetail(templateId),
        previewWorkLogTemplateViewers(templateId)
      ]).then(([templateResponse, viewerResponse]) => {
        const template = (templateResponse && templateResponse.data) || {}
        const viewer = (viewerResponse && viewerResponse.data) || {}
        this.currentTemplate = template
        this.form.logType = 'DAILY'
        this.formFields = ((template.fields || []).filter(item => Number(item.status) === 1)).sort((a, b) => Number(a.sortNo || 0) - Number(b.sortNo || 0))
        const currentContent = this.form.contentMap || {}
        const content = {}
        this.formFields.forEach(field => {
          this.$set(content, field.fieldKey, currentContent[field.fieldKey] !== undefined ? currentContent[field.fieldKey] : this.defaultValue(field))
        })
        this.form.contentMap = content
        const leaderUserIds = this.normalizeIds(viewer.leaderUserIds)
        const fixedReceiverUserIds = this.mergeIds(this.normalizeIds(viewer.fixedReceiverUserIds || viewer.receiverUserIds), leaderUserIds)
        this.viewerConfig = {
          allowCustomViewer: Number(viewer.allowCustomViewer) === 0 ? 0 : 1,
          receiverRequiredFlag: Number(viewer.receiverRequiredFlag) === 0 ? 0 : 1,
          leaderVisibleFlag: Number(viewer.leaderVisibleFlag) === 1 ? 1 : 0,
          currentUserId: viewer.currentUserId === undefined || viewer.currentUserId === null ? null : Number(viewer.currentUserId),
          fixedReceiverUserIds,
          fixedCcUserIds: [],
          leaderUserIds,
          receiverUserIds: this.normalizeIds(viewer.receiverUserIds),
          ccUserIds: []
        }
        this.form.receiverUserIds = this.extraViewerIds(this.form.id ? this.form.receiverUserIds : [], 'receiver')
        this.form.ccUserIds = []
        this.form.periodStartDate = ''
        this.form.periodEndDate = ''
        this.form.periodRange = []
        if (!this.form.periodDate) {
          this.form.periodDate = this.today()
        }
      })
    },
    syncPeriodRange(value) {
      this.form.periodStartDate = value && value.length > 0 ? value[0] : ''
      this.form.periodEndDate = value && value.length > 1 ? value[1] : ''
    },
    handleScheduleSwitch(enabled) {
      if (enabled && !this.isFutureScheduleTime(this.scheduleSubmitTime)) {
        this.scheduleSubmitTime = this.defaultScheduleSubmitTime()
      }
    },
    handleScheduleTimeChange() {
      if (this.scheduleEnabled && !this.isFutureScheduleTime(this.scheduleSubmitTime)) {
        this.scheduleSubmitTime = this.formatDateTime(this.futureScheduleDate(MIN_SCHEDULE_DELAY_MS))
      }
    },
    async submitForm(submitted) {
      if (this.isSubmittedEdit) {
        submitted = true
      }
      if (!this.checkForm(submitted)) {
        return
      }
      this.autoSavePaused = true
      if (!this.isSubmittedEdit) {
        await this.flushAutoSave(true, true)
      }
      if (submitted && this.scheduleEnabled && !this.isSubmittedEdit) {
        this.submitScheduledForm()
        return
      }
      this.submitLoading = true
      const api = submitted ? submitWorkLog : saveWorkLogDraft
      api(this.buildSavePayload())
        .then(() => {
          this.$message.success(this.isSubmittedEdit ? '保存成功' : '提交成功')
          this.autoSavePaused = true
          this.formOpen = false
          this.getList()
        })
        .finally(() => {
          this.submitLoading = false
          if (this.formOpen) {
            this.autoSavePaused = false
          }
        })
    },
    async submitScheduledForm() {
      const ready = await this.ensureScheduleBeforeSubmit()
      if (!ready) {
        this.autoSavePaused = false
        return
      }
      this.submitLoading = true
      scheduleSubmitWorkLog({
        ...this.buildSavePayload(),
        scheduleSubmitTime: this.scheduleSubmitTime
      })
        .then(() => {
          this.$message.success('已设置定时发送')
          this.autoSavePaused = true
          this.formOpen = false
          this.getList()
        })
        .finally(() => {
          this.submitLoading = false
          if (this.formOpen) {
            this.autoSavePaused = false
          }
        })
    },
    submitExisting(row) {
      this.handleEdit(row)
    },
    isDraftLog(row) {
      return Number(row && row.status) === 1
    },
    handleRemove(row) {
      this.$confirm(`确认删除「${row.title || '该日志'}」？`, '提示', { type: 'warning' })
        .then(() => removeWorkLog(row.id))
        .then(() => {
          this.$message.success('删除成功')
          this.getList()
        })
        .catch(error => {
          if (error !== 'cancel' && error !== 'close') {
            this.getList()
          }
        })
    },
    buildSavePayload() {
      const payload = {
        id: this.form.id,
        templateId: this.form.templateId,
        periodDate: this.form.periodDate || this.today(),
        periodStartDate: null,
        periodEndDate: null,
        title: this.form.title,
        contentMap: this.form.contentMap,
        receiverUserIds: this.finalReceiverIds(),
        ccUserIds: [],
        attachmentFileIds: this.form.attachmentFileIds
      }
      return payload
    },
    checkForm(submitted) {
      if (!this.form.templateId) {
        this.$message.warning('请选择日志模板')
        return false
      }
      if (!submitted) {
        return true
      }
      for (const field of this.formFields) {
        if (Number(field.requiredFlag) === 1 && this.isBlank(this.form.contentMap[field.fieldKey])) {
          this.$message.warning(`${field.fieldName}不能为空`)
          return false
        }
      }
      if (this.viewerConfig.receiverRequiredFlag === 1 && !this.finalReceiverIds().length) {
        this.$message.warning('发送给不能为空')
        return false
      }
      return true
    },
    async ensureScheduleBeforeSubmit() {
      if (!this.scheduleSubmitTime) {
        this.$message.warning('请选择定时发送时间')
        return false
      }
      if (this.isFutureScheduleTime(this.scheduleSubmitTime)) {
        return true
      }
      const nextTime = this.formatDateTime(this.futureScheduleDate(MIN_SCHEDULE_DELAY_MS))
      this.scheduleSubmitTime = nextTime
      try {
        await this.$confirm(`当前选择的发送时间已经不足5分钟，系统已顺延到 ${nextTime}，是否继续定时发送？`, '发送时间已调整', {
          type: 'warning',
          confirmButtonText: '按新时间发送',
          cancelButtonText: '我再改改'
        })
        return true
      } catch (e) {
        return false
      }
    },
    fieldOptions(field) {
      if (!field || !field.optionsJson) {
        return []
      }
      try {
        const parsed = JSON.parse(field.optionsJson)
        if (!Array.isArray(parsed)) {
          return []
        }
        return parsed.map(item => {
          if (typeof item === 'string' || typeof item === 'number') {
            return { label: String(item), value: item }
          }
          return {
            label: item.label || item.name || item.value,
            value: item.value !== undefined ? item.value : (item.label || item.name)
          }
        }).filter(item => item.label !== undefined && item.value !== undefined)
      } catch (e) {
        return []
      }
    },
    defaultValue(field) {
      if (this.isUploadField(field) || field.fieldType === 'multiselect') {
        return field.defaultValue ? [field.defaultValue] : []
      }
      return field.defaultValue || ''
    },
    isUploadField(field) {
      return field && (field.fieldType === 'file' || field.fieldType === 'image')
    },
    uploadAccept(field) {
      return field && field.fieldType === 'image' ? 'image/*' : ''
    },
    resetSchedule(detail) {
      const pending = detail && Number(detail.status) !== 2 && Number(detail.scheduleStatus) === 1 && detail.scheduleSubmitTime
      this.scheduleEnabled = Boolean(pending)
      this.scheduleSubmitTime = pending ? detail.scheduleSubmitTime : this.defaultScheduleSubmitTime()
    },
    resetAutoSaveState(paused) {
      if (this.autoSaveTimer) {
        clearTimeout(this.autoSaveTimer)
        this.autoSaveTimer = null
      }
      this.autoSavePaused = Boolean(paused)
      this.autoSaveSaving = false
      this.autoSaveLastSnapshot = ''
      this.autoSavePromise = null
    },
    scheduleAutoSave() {
      if (!this.canAutoSave()) {
        return
      }
      const snapshot = this.autoSavePayloadSnapshot()
      if (!snapshot || snapshot === this.autoSaveLastSnapshot) {
        return
      }
      if (this.autoSaveTimer) {
        clearTimeout(this.autoSaveTimer)
      }
      this.autoSaveTimer = setTimeout(() => {
        this.flushAutoSave(false)
      }, 1200)
    },
    canAutoSave() {
      return this.formOpen &&
        !this.autoSavePaused &&
        !this.submitLoading &&
        !this.isSubmittedEdit &&
        this.form &&
        this.form.templateId &&
        this.hasAutoSaveContent()
    },
    hasAutoSaveContent() {
      const content = this.form && this.form.contentMap ? this.form.contentMap : {}
      const hasContent = Object.keys(content).some(key => !this.isBlank(content[key]))
      return hasContent ||
        !this.isBlank(this.form.title) ||
        !this.isBlank(this.form.attachmentFileIds) ||
        (Array.isArray(this.form.receiverUserIds) && this.form.receiverUserIds.length > 0)
    },
    autoSavePayloadSnapshot() {
      if (!this.form || !this.form.templateId) {
        return ''
      }
      try {
        return JSON.stringify(this.buildSavePayload())
      } catch (e) {
        return ''
      }
    },
    async flushAutoSave(force, allowPaused) {
      if (this.autoSaveTimer) {
        clearTimeout(this.autoSaveTimer)
        this.autoSaveTimer = null
      }
      if (this.autoSavePaused && !allowPaused) {
        return
      }
      if ((!force && !this.canAutoSave()) || !this.form || !this.form.templateId || this.isSubmittedEdit || !this.hasAutoSaveContent()) {
        return
      }
      const snapshot = this.autoSavePayloadSnapshot()
      if (!snapshot || (!force && snapshot === this.autoSaveLastSnapshot)) {
        return
      }
      if (this.autoSaveSaving && this.autoSavePromise) {
        await this.autoSavePromise
        return
      }
      this.autoSaveSaving = true
      const payload = this.buildSavePayload()
      this.autoSavePromise = saveWorkLogDraft(payload)
        .then(response => {
          const id = response && response.data
          this.autoSavePaused = true
          if (id && !this.form.id) {
            this.form.id = id
          }
          this.$nextTick(() => {
            this.autoSaveLastSnapshot = this.autoSavePayloadSnapshot()
            this.autoSavePaused = false
          })
        })
        .catch(() => {})
        .finally(() => {
          this.autoSaveSaving = false
          this.autoSavePromise = null
        })
      await this.autoSavePromise
    },
    async restoreLatestAutoSave(templateId) {
      if (!templateId || this.form.id || this.isSubmittedEdit) {
        return
      }
      try {
        const listResponse = await listMyWorkLogs({
          pageNum: 1,
          pageSize: 1,
          templateId,
          autoDraftOnly: true
        })
        const row = listResponse && listResponse.rows && listResponse.rows[0]
        if (!row || !row.id) {
          this.autoSaveLastSnapshot = this.autoSavePayloadSnapshot()
          return
        }
        const detailResponse = await getWorkLogDetail(row.id)
        const detail = (detailResponse && detailResponse.data) || {}
        if (Number(detail.status) !== 1) {
          return
        }
        this.autoSavePaused = true
        const valueMap = {}
        ;(detail.fieldValues || []).forEach(item => {
          valueMap[item.fieldKey] = item.value
        })
        const content = {}
        this.formFields.forEach(field => {
          this.$set(content, field.fieldKey, valueMap[field.fieldKey] !== undefined ? valueMap[field.fieldKey] : this.defaultValue(field))
        })
        this.form = {
          ...this.form,
          id: detail.id,
          status: detail.status,
          title: detail.title || '',
          receiverUserIds: this.extraViewerIds(this.normalizeIds(detail.receiverUserIds), 'receiver'),
          attachmentFileIds: detail.attachmentFileIds || '',
          contentMap: content
        }
        this.resetSchedule(detail)
        this.$nextTick(() => {
          this.autoSaveLastSnapshot = this.autoSavePayloadSnapshot()
          this.autoSavePaused = false
        })
      } catch (e) {
        this.autoSaveLastSnapshot = this.autoSavePayloadSnapshot()
      }
    },
    defaultScheduleSubmitTime() {
      return this.formatDateTime(this.futureScheduleDate(DEFAULT_SCHEDULE_DELAY_MS))
    },
    isFutureScheduleTime(value) {
      const date = this.parseDateTime(value)
      return !!date && date.getTime() >= Date.now() + MIN_SCHEDULE_DELAY_MS
    },
    futureScheduleDate(delayMs) {
      const date = new Date(Date.now() + delayMs)
      if (date.getSeconds() || date.getMilliseconds()) {
        date.setMinutes(date.getMinutes() + 1)
      }
      date.setSeconds(0, 0)
      return date
    },
    parseDateTime(value) {
      if (!value) {
        return null
      }
      const date = new Date(String(value).replace(/-/g, '/'))
      return Number.isNaN(date.getTime()) ? null : date
    },
    formatDateTime(date) {
      const pad = value => `${value}`.padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:00`
    },
    formatClock(date) {
      const pad = value => `${value}`.padStart(2, '0')
      return `${pad(date.getHours())}:${pad(date.getMinutes())}:00`
    },
    displayMinuteTime(value) {
      if (!value) {
        return '-'
      }
      return String(value).replace('T', ' ').slice(0, 16)
    },
    handleFieldFileChange(fieldKey, files) {
      this.$set(this.form.contentMap, fieldKey, files || [])
    },
    handleReceiverChange(value) {
      this.form.receiverUserIds = this.extraViewerIds(value, 'receiver')
    },
    removeReceiverUser(userId) {
      this.form.receiverUserIds = this.excludeIds(this.form.receiverUserIds, [userId])
    },
    toggleReceiverPicker() {
      this.receiverPickerOpen = !this.receiverPickerOpen
    },
    selectableUsers() {
      const excludes = new Set(this.mergeIds(this.fixedViewerIds, [this.viewerConfig.currentUserId]))
      return (this.staffOptions || []).filter(user => !excludes.has(Number(user.userId)))
    },
    viewerCards(ids, fixed) {
      return this.normalizeIds(ids).map(id => {
        const user = this.userById(id)
        const name = user ? (user.nickName || user.userName || String(id)) : String(id)
        return {
          id,
          name,
          avatar: user ? (user.avatar || user.headImage || user.wechatAvatar || '') : '',
          shortName: this.shortName(name),
          fixed
        }
      })
    },
    userById(id) {
      return (this.staffOptions || []).find(item => Number(item.userId) === Number(id))
    },
    userName(id) {
      const user = this.userById(id)
      return user ? user.nickName : String(id)
    },
    shortName(name) {
      const text = String(name || '')
      return text.length > 2 ? text.slice(text.length - 2) : (text || '无')
    },
    extraViewerIds(ids) {
      const excludes = this.mergeIds(this.fixedViewerIds, [this.viewerConfig.currentUserId])
      return this.excludeIds(ids, excludes)
    },
    finalReceiverIds() {
      return this.mergeIds(this.viewerConfig.fixedReceiverUserIds, this.form.receiverUserIds)
    },
    isBlank(value) {
      if (value === undefined || value === null) {
        return true
      }
      if (Array.isArray(value)) {
        return value.length === 0
      }
      return String(value).trim() === ''
    },
    normalizeIds(value) {
      if (!value) {
        return []
      }
      if (Array.isArray(value)) {
        return value
          .filter(item => item !== undefined && item !== null && item !== '')
          .map(item => Number(item))
          .filter(item => !Number.isNaN(item))
      }
      return []
    },
    mergeIds(...groups) {
      const result = []
      groups.forEach(group => {
        this.normalizeIds(group).forEach(id => {
          if (!result.includes(id)) {
            result.push(id)
          }
        })
      })
      return result
    },
    excludeIds(ids, excludes) {
      const excludeSet = new Set(this.normalizeIds(excludes))
      return this.normalizeIds(ids).filter(id => !excludeSet.has(id))
    },
    periodText(row) {
      if (row.logType === 'WEEKLY') {
        return [row.periodStartDate, row.periodEndDate].filter(Boolean).join(' 至 ') || '-'
      }
      return row.periodDate || '-'
    },
    statusText(status) {
      const map = { 1: '未提交', 2: '已提交', 3: '已撤回' }
      return map[Number(status)] || '-'
    },
    statusTag(status) {
      const map = { 1: 'info', 2: 'success', 3: 'warning' }
      return map[Number(status)] || 'info'
    },
    contentPreview(row) {
      const text = String((row && row.plainContent) || '').replace(/\s+/g, ' ').trim()
      if (!text) {
        return '暂无内容摘要'
      }
      return text.length > 86 ? `${text.slice(0, 86)}...` : text
    },
    today() {
      const date = new Date()
      const month = `${date.getMonth() + 1}`.padStart(2, '0')
      const day = `${date.getDate()}`.padStart(2, '0')
      return `${date.getFullYear()}-${month}-${day}`
    },
    cleanParams(params) {
      const result = {}
      Object.keys(params || {}).forEach(key => {
        const value = params[key]
        if (value !== '' && value !== null && value !== undefined) {
          result[key] = value
        }
      })
      return result
    }
  }
}
</script>

<style scoped>
.work-log-page {
  background: #fff;
}

.work-log-form {
  padding-right: 8px;
}

.work-log-table ::v-deep .el-table__row td {
  padding-top: 10px;
  padding-bottom: 10px;
}

.log-title-cell {
  padding: 2px 0;
}

.log-row-title {
  overflow: hidden;
  color: #1f2937;
  font-size: 14px;
  font-weight: 600;
  line-height: 22px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.log-row-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 5px;
  color: #8a95a6;
  font-size: 12px;
  line-height: 20px;
}

.log-row-preview {
  overflow: hidden;
  margin-top: 5px;
  color: #5f6f86;
  font-size: 13px;
  line-height: 20px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.viewer-strip {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 16px;
  min-height: 82px;
  padding: 2px 0 4px;
}

.viewer-card {
  width: 64px;
  text-align: center;
}

.viewer-avatar-wrap {
  position: relative;
  width: 54px;
  height: 54px;
  margin: 0 auto 6px;
}

.viewer-avatar-img,
.viewer-avatar-fallback,
.viewer-add-card {
  width: 54px;
  height: 54px;
  border-radius: 8px;
  box-sizing: border-box;
}

.viewer-avatar-img {
  display: block;
  object-fit: cover;
}

.viewer-avatar-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #4480f3;
  color: #fff;
  font-size: 15px;
  font-weight: 600;
}

.viewer-lock,
.viewer-remove {
  position: absolute;
  top: -7px;
  right: -7px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: 0;
  border-radius: 50%;
  color: #fff;
  font-size: 13px;
  line-height: 22px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.16);
}

.viewer-lock {
  background: #2f8cf5;
}

.viewer-remove {
  padding: 0;
  background: #9aa1aa;
  cursor: pointer;
}

.viewer-name {
  overflow: hidden;
  color: #1f2937;
  font-size: 13px;
  line-height: 18px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.viewer-add-card {
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #d8dce5;
  background: #fff;
  color: #c5cad3;
  font-size: 28px;
  cursor: pointer;
}

.viewer-picker-actions {
  margin-top: 8px;
  text-align: right;
}

.danger {
  color: #f56c6c;
}
</style>
